import type { OrderDetailsUI } from "@/domain/order/order-details.model"
import { formatOrderDetailsDateTime } from "@/utils/format-order-details-datetime"
import { PaymentTiming } from "@/types/enums/PaymentTiming"

export type OrderDetailGridRow = {
  label: string
  value: string
  /** When set, the grid renders a payment timing badge instead of plain text. */
  paymentTiming?: PaymentTiming
}

/**
 * Label/value pairs for delivery / contact meta (empty values omitted).
 * Does not expose internal UUIDs.
 */
export function getOrderDetailGridRows(order: OrderDetailsUI): OrderDetailGridRow[] {
  const rows: OrderDetailGridRow[] = []

  if (order.customerName.trim()) {
    rows.push({ label: "Customer", value: order.customerName.trim() })
  }

  if (order.contactNumber.trim()) {
    rows.push({ label: "Phone", value: order.contactNumber.trim() })
  }

  // A Pickup order has no delivery destination (the page shows "Collect from" instead), so its
  // stored address columns are never presented as one.
  if (!order.isPickup) {
    if (order.address.trim()) {
      rows.push({ label: "Delivery address", value: order.address.trim() })
    }

    const cityZip = [order.city, order.zipCode].filter(Boolean).join(", ").trim()
    if (cityZip) {
      rows.push({ label: "City / ZIP", value: cityZip })
    }

    if (order.country.trim()) {
      rows.push({ label: "Country", value: order.country.trim() })
    }
  }

  const placed = formatOrderDetailsDateTime(order.createdDate)
  if (placed) {
    rows.push({ label: "Order date", value: placed })
  }

  if (order.orderTypeLabel.trim()) {
    rows.push({ label: "Order type", value: order.orderTypeLabel.trim() })
  }

  if (order.orderChannelLabel.trim()) {
    rows.push({ label: "Order channel", value: order.orderChannelLabel.trim() })
  }



  const instr = order.instructions.trim()
  if (instr && instr.toLowerCase() !== "none") {
    rows.push({ label: "Order note", value: instr })
  }

  return rows
}
